import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { BOOKING_URL } from '../data/services';

const FOUNDER_IMAGE = "https://images.pexels.com/photos/32428850/pexels-photo-32428850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CLINIC_IMAGE = "https://images.pexels.com/photos/3997991/pexels-photo-3997991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="about-page" className="bg-chalk min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-cinzel text-amaranth text-xs tracking-[0.3em] mb-4">
              OUR STORY
            </p>
            <h1 className="font-serif text-dark text-4xl lg:text-6xl mb-6">
              About Aphrodite
            </h1>
            <p className="text-dark-secondary max-w-2xl mx-auto text-lg">
              Where science meets the divine, and every guest discovers 
              their own inner goddess.
            </p>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="aspect-[21/9] overflow-hidden"
          >
            <img
              src={CLINIC_IMAGE}
              alt="Aphrodite Med Spa Interior"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 lg:py-24 px-6 bg-light">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-dark text-3xl lg:text-4xl mb-8">
                Our Philosophy
              </h2>
              <div className="space-y-6 text-dark-secondary leading-relaxed">
                <p>
                  At Aphrodite Med Spa, we believe that true beauty is an expression 
                  of inner vitality—not something that can be artificially imposed, but 
                  rather revealed through expert care and science-backed treatments.
                </p>
                <p>
                  Named after the Greek goddess of beauty and love, our sanctuary in 
                  the heart of Canoga Park offers a refuge from the ordinary. Here, 
                  every treatment is designed to help you reconnect with your most 
                  radiant, confident self.
                </p>
                <p>
                  We combine the artistry of aesthetic medicine with the wisdom of 
                  holistic wellness, understanding that lasting beauty begins from 
                  within. Our approach is never about transformation—it's about 
                  revelation.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { number: "10+", label: "Years Experience" },
                { number: "5000+", label: "Happy Guests" },
                { number: "20+", label: "Treatments Offered" },
                { number: "100%", label: "Satisfaction Focus" }
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-chalk p-8 text-center"
                >
                  <p className="font-serif text-amaranth text-4xl mb-2">
                    {stat.number}
                  </p>
                  <p className="text-dark-secondary text-sm">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 lg:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative order-2 lg:order-1"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={FOUNDER_IMAGE}
                  alt="Founder"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-thulian -z-10" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <p className="font-cinzel text-amaranth text-xs tracking-[0.3em] mb-4">
                THE FOUNDER
              </p>
              <h2 className="font-serif text-dark text-3xl lg:text-4xl mb-8">
                A Vision of Elevated Beauty
              </h2>
              <div className="space-y-6 text-dark-secondary leading-relaxed">
                <p>
                  Aphrodite Med Spa was founded with a singular vision: to create a 
                  space where luxury meets medical excellence, where every guest feels 
                  seen, heard, and transformed.
                </p>
                <p>
                  With over a decade of experience in aesthetic medicine and a deep 
                  passion for holistic wellness, our founder recognized a gap in the 
                  Los Angeles beauty landscape—a need for a med spa that treats the 
                  whole person, not just the surface.
                </p>
                <p>
                  "I wanted to create more than a clinic. I wanted to build a sanctuary 
                  where women could reconnect with their power, their beauty, and their 
                  goddess energy. Every treatment we offer is designed with this 
                  intention."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 px-6 bg-olive/10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-dark text-3xl lg:text-4xl">
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Excellence",
                description: "We use only the finest products and most advanced techniques, delivered by highly trained professionals."
              },
              {
                title: "Authenticity",
                description: "We celebrate your unique beauty. Our goal is enhancement, never erasure of what makes you, you."
              },
              {
                title: "Wellness",
                description: "True beauty radiates from within. We integrate longevity medicine to help you thrive inside and out."
              },
              {
                title: "Luxury",
                description: "Every detail of your experience is curated for comfort, elegance, and transformation."
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-thulian/20 rotate-45 flex items-center justify-center">
                  <span className="text-amaranth font-serif text-xl -rotate-45">
                    {index + 1}
                  </span>
                </div>
                <h3 className="font-serif text-dark text-xl mb-3">
                  {value.title}
                </h3>
                <p className="text-dark-secondary text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 px-6 bg-amaranth text-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="font-serif text-4xl lg:text-5xl mb-6">
            Begin Your Journey
          </h2>
          <p className="text-white/80 mb-8">
            Schedule your complimentary consultation and discover how Aphrodite 
            Med Spa can help you reveal your most radiant self.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-book-btn"
              className="btn-luxury inline-flex items-center justify-center gap-2 bg-white text-amaranth px-8 py-4 rounded-full font-medium"
            >
              Book Consultation
              <ArrowRight size={18} />
            </a>
            <Link
              to="/services"
              data-testid="about-services-link"
              className="inline-flex items-center justify-center gap-2 border border-white/50 text-white px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-colors"
            >
              Explore Services
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default AboutPage;
